import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { customerService, getErrorMessage } from '@/services';
import type { CustomerFilters, CustomerCreateRequest } from '@/types';

export function useCustomers(page: number = 1, filters?: CustomerFilters) {
  return useQuery({
    queryKey: ['customers', page, filters],
    queryFn: () => customerService.getCustomers(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => customerService.getCustomer(id),
    // The string "undefined" (e.g. a stale /customers/${newCustomer.id}
    // navigation where newCustomer.id was missing) passes `!!id` -- guard
    // against it explicitly so we don't fire a doomed GET /customers/undefined/.
    enabled: !!id && id !== 'undefined',
  });
}

export function useCustomerPolicies(id: string) {
  return useQuery({
    queryKey: ['customer-policies', id],
    queryFn: () => customerService.getCustomerPolicies(id),
    enabled: !!id && id !== 'undefined',
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CustomerCreateRequest) => customerService.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Customer created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomerCreateRequest> }) =>
      customerService.updateCustomer(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', variables.id] });
      toast.success('Customer updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerService.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Customer deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useToggleCustomerActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerService.toggleActive(id),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', updated.id] });
      toast.success(updated.is_active ? 'Customer activated' : 'Customer deactivated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useCustomerMe() {
  return useQuery({
    queryKey: ['customer', 'me'],
    queryFn: () => customerService.getMe(),
  });
}

export function useUpdateCustomerMe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<import('@/types').CustomerCreateRequest>) => customerService.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', 'me'] });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** Item 8: provision (or link) a login for a customer. Broker-admin only; see CustomerLoginSection. */
export function useCreateCustomerLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerService.createLogin(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
